import React from 'react';
import {useNavigate} from 'react-router-dom';
import {ComposedChart, Line, Area, XAxis, ResponsiveContainer} from 'recharts';
import {filterTestsByCategory, HEALTH_CATEGORIES} from '@/utils/healthCategories';
import {useDashboardData} from '@/hooks/useDashboardData';
import {useAppSelector} from '@/hooks/redux';
import {selectSubscription} from '@/slices/testSlice';
import {determineStatus, cleanBiomarkerName} from '@/utils/biomarkerUtils';
import {getCustomBiomarkerConfig} from '@/utils/customBiomarkers';
import {BiomarkerStatus} from '@/types/biomarker';
import {useAuth} from '@/hooks/useAuth';

interface BiomarkerDetailChartProps {
    selectedCategory: string;
    onBiomarkerSelect?: (biomarkerName: string) => void;
    selectedBiomarkerName?: string | null;
}

// Helper function to get status text with custom label support
const getStatusText = (status: BiomarkerStatus, testName?: string, gender?: 'male' | 'female'): string => {
    // If test name is provided, check for custom label
    if (testName) {
        const customConfig = getCustomBiomarkerConfig(testName, gender);
        if (customConfig && customConfig.zones.length > 0) {
            // Find the zone that matches this status
            const matchingZone = customConfig.zones.find((zone) => zone.status === status);
            if (matchingZone) {
                return matchingZone.label;
            }
        }
    }

    // Fall back to default labels
    switch (status) {
        case 'Normal':
        case 'Optimal':
            return 'Optimal';
        case 'High':
            return 'High';
        case 'Low':
            return 'Low';
        case 'Borderline':
            return 'Borderline';
        case 'Elevated':
            return 'Elevated';
        default:
            return 'Unknown';
    }
};

// Helper function to get chart gradient color based on status
const getChartGradient = (status: BiomarkerStatus, index: number): string => {
    switch (status) {
        case 'Normal':
        case 'Optimal':
        case 'Negative': // Negative disease markers (good result)
            return `heartHealthGradient-normal-${index}`; // Green
        case 'High':
        case 'Positive': // Positive disease markers (elevated)
        case 'Toxic': // Toxic levels (e.g., high Vitamin D)
            return `heartHealthGradient-high-${index}`; // Red
        case 'Low':
        case 'Elevated':
        case 'Deficient': // Vitamin/mineral deficiencies
        case 'Impaired': // Impaired function (e.g., low eGFR)
            return `heartHealthGradient-low-${index}`; // Orange
        case 'Borderline':
            return `heartHealthGradient-borderline-${index}`; // Yellow
        default:
            return `heartHealthGradient-unknown-${index}`; // Gray
    }
};

// Helper function to get chart gradient colors
const getGradientColors = (status: BiomarkerStatus) => {
    switch (status) {
        case 'Normal':
        case 'Optimal':
        case 'Negative': // Negative disease markers (good result)
            return {primary: '#42D4B0', secondary: '#42D4B0'}; // Green
        case 'High':
        case 'Positive': // Positive disease markers (elevated)
        case 'Toxic': // Toxic levels (e.g., high Vitamin D)
            return {primary: '#F15E87', secondary: '#F15E87'}; // Red
        case 'Low':
        case 'Elevated':
        case 'Deficient': // Vitamin/mineral deficiencies
        case 'Impaired': // Impaired function (e.g., low eGFR)
            return {primary: '#FFBE7A', secondary: '#FFBE7A'}; // Orange
        case 'Borderline':
            return {primary: '#FFBE7A', secondary: '#FFBE7A'}; // Orange
        default:
            return {primary: '#3B82F6', secondary: '#3B82F6'}; // Blue (fallback)
    }
};

// Mock history data for charts - matches single results page
const getMockHistory = (currentValue: number) => {
    const baseValue = parseFloat(String(currentValue));
    if (isNaN(baseValue)) return [];

    const roundToOne = (num: number) => Math.round(num * 10) / 10;

    return [
        {date: 'Dec 24', value: roundToOne(baseValue * 0.85), label: "Dec '24"},
        {date: 'Feb 25', value: roundToOne(baseValue * 0.92), label: "Feb '25"},
        {date: 'Jun 25', value: roundToOne(baseValue * 0.88), label: "Jun '25"},
        {date: 'Jul 25', value: roundToOne(baseValue * 1.02), label: "Jul '25"},
    ];
};

const BiomarkerDetailChart: React.FC<BiomarkerDetailChartProps> = ({
    selectedCategory,
    onBiomarkerSelect,
    selectedBiomarkerName,
}) => {
    const navigate = useNavigate();
    const subscription = useAppSelector(selectSubscription);
    const {user} = useAuth();

    // Get gender from user info for gender-specific biomarker configs
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Use dashboard-specific data (dummy data if not subscribed)
    const {data: currentPatientData} = useDashboardData();

    // Get all results for the current patient
    const allResults = React.useMemo(() => {
        if (!currentPatientData || !Array.isArray(currentPatientData)) {
            return [];
        }
        return currentPatientData.flatMap(
            (test: any) =>
                test.results?.map((result: any) => ({
                    ...result,
                    createdDate: test.createdDate,
                })) || []
        );
    }, [currentPatientData]);

    // Filter results by selected category and group by biomarker name
    const categoryResults = React.useMemo(() => {
        const filteredResults = filterTestsByCategory(allResults, selectedCategory);

        // Group by test_name to consolidate duplicates
        const groupedResults = filteredResults.reduce(
            (acc, result) => {
                const testName = result.test_name;
                if (!acc[testName]) {
                    acc[testName] = [];
                }
                acc[testName].push(result);
                return acc;
            },
            {} as Record<string, any[]>
        );

        // Return one biomarker per test name with real historical data when available
        return Object.entries(groupedResults).map(([, results]) => {
            // Sort by statusDate (biomarker timing), fallback to createdDate and get the most recent result as the main display
            const sortedResults = results.sort(
                (a, b) =>
                    new Date(b.statusDate || b.createdDate).getTime() -
                    new Date(a.statusDate || a.createdDate).getTime()
            );

            const mostRecentResult = sortedResults[0];
            const currentValue = parseFloat(mostRecentResult.result);

            // Use appropriate data based on subscription status and data availability
            let historicalData;
            if (subscription && sortedResults.length > 1) {
                // Subscriber with real historical data available - exclude most recent since it's shown as "Current"
                const historicalResults = sortedResults.slice(1); // Remove most recent result
                historicalData = historicalResults
                    .sort(
                        (a, b) =>
                            new Date(a.statusDate || a.createdDate).getTime() -
                            new Date(b.statusDate || b.createdDate).getTime()
                    ) // Oldest to newest
                    .map((result) => ({
                        date: result.statusDate || result.createdDate,
                        value: parseFloat(result.result) || 0,
                        month: new Date(result.statusDate || result.createdDate).toLocaleDateString('en-US', {
                            month: 'short',
                            year: '2-digit',
                        }),
                    }));
            } else {
                // Non-subscriber or subscriber without sufficient history - use mock data
                historicalData = getMockHistory(currentValue);
            }

            return {
                ...mostRecentResult, // Most recent result
                historicalData: historicalData,
            };
        });
    }, [allResults, selectedCategory, subscription]);

    // Get category info
    const categoryInfo = HEALTH_CATEGORIES[selectedCategory];

    // Handle Results button click - notify parent or navigate to the test
    const handleResultsClick = (biomarker: any) => {
        // If onBiomarkerSelect is provided, call it to update Lab Results tile
        if (onBiomarkerSelect) {
            onBiomarkerSelect(biomarker.test_name);
            return;
        }

        // Otherwise, navigate to the full result page
        if (!currentPatientData || !Array.isArray(currentPatientData)) {
            return;
        }

        // Find the test that contains this biomarker
        const testWithBiomarker = currentPatientData.find((test: any) =>
            test.results?.some((result: any) => result.test_name === biomarker.test_name)
        );

        if (testWithBiomarker) {
            navigate(`/result/${testWithBiomarker.id}`);
        }
    };

    // Auto-select first biomarker when category changes
    // Use a ref to track the previous category to prevent loops
    const prevCategoryRef = React.useRef<string>(selectedCategory);
    React.useEffect(() => {
        // Only trigger when category actually changes, not on every render
        if (selectedCategory !== prevCategoryRef.current) {
            prevCategoryRef.current = selectedCategory;
            if (onBiomarkerSelect && categoryResults.length > 0) {
                // Check if the currently selected biomarker is already in this category
                // If so, don't auto-select (user selected this biomarker and we changed category to match)
                const selectedIsInCategory =
                    selectedBiomarkerName &&
                    categoryResults.some((b) => cleanBiomarkerName(b.test_name) === selectedBiomarkerName);

                if (!selectedIsInCategory) {
                    onBiomarkerSelect(categoryResults[0].test_name);
                }
            }
        }
    }, [selectedCategory, categoryResults, onBiomarkerSelect, selectedBiomarkerName]);

    // Show loading state if data is not yet available
    if (!currentPatientData) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">Loading biomarkers...</p>
            </div>
        );
    }

    if (!categoryInfo) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">Category not found</p>
            </div>
        );
    }

    if (categoryResults.length === 0) {
        return (
            <div className="flex items-center justify-center h-32 text-gray-500">
                <p className="text-sm">No biomarkers available for {categoryInfo.name}</p>
            </div>
        );
    }

    return (
        <div>
            {categoryResults.map((biomarker, index) => {
                // Use the same status determination logic as biomarkerUtils (with gender for gender-specific biomarkers)
                const status = determineStatus(biomarker, gender);
                const statusText = getStatusText(status, biomarker.test_name, gender);
                const chartGradientId = getChartGradient(status, index);
                const gradientColors = getGradientColors(status);

                return (
                    <div
                        key={`${biomarker.test_name}-${index}`}
                        className="py-3 px-4 border-b border-mld-panel-border last:border-b-0 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => handleResultsClick(biomarker)}>
                        {/* Desktop Layout: Single Row */}
                        <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                            {/* Column 1: Test name and result */}
                            <div className="col-span-3">
                                <h4 className="text-sm font-medium text-blue-950 mb-1">
                                    {cleanBiomarkerName(biomarker.test_name)}
                                </h4>
                                <div className="flex items-baseline space-x-1">
                                    <span className="text-lg font-semibold text-blue-950">
                                        {(() => {
                                            const num = parseFloat(biomarker.result);
                                            return isNaN(num) ? biomarker.result : num.toFixed(1);
                                        })()}
                                    </span>
                                    {biomarker.units && (
                                        <span className="text-lg font-semibold text-blue-950">{biomarker.units}</span>
                                    )}
                                </div>
                            </div>

                            {/* Column 2: Historical trend chart */}
                            <div className="col-span-7 flex justify-center">
                                <div className="h-16 w-full rounded-lg border-mld-panel-border border overflow-hidden">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart
                                            data={(() => {
                                                // Transform data to match single results page format
                                                const histData = biomarker.historicalData || [];
                                                const currentValue = parseFloat(biomarker.result) || 0;

                                                // Take last 3 historical points and add current
                                                const transformedData = [
                                                    ...histData.slice(-3).map((point: any) => ({
                                                        name: point.label || point.month || point.date,
                                                        value: point.value,
                                                    })),
                                                    {
                                                        name: 'Current',
                                                        value: currentValue,
                                                    },
                                                ];

                                                return transformedData;
                                            })()}
                                            margin={{top: 6, right: 0, left: 0, bottom: 6}}>
                                            <defs>
                                                <linearGradient id={chartGradientId} x1="0" y1="0" x2="0" y2="1">
                                                    <stop
                                                        offset="5%"
                                                        stopColor={gradientColors.primary}
                                                        stopOpacity={0.3}
                                                    />
                                                    <stop
                                                        offset="95%"
                                                        stopColor={gradientColors.secondary}
                                                        stopOpacity={0.05}
                                                    />
                                                </linearGradient>
                                            </defs>
                                            <XAxis hide />
                                            <Area
                                                type="monotone"
                                                dataKey="value"
                                                stroke="none"
                                                fill={`url(#${chartGradientId})`}
                                                connectNulls={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="value"
                                                stroke={gradientColors.primary}
                                                strokeWidth={1}
                                                dot={false}
                                                activeDot={false}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Column 3: Status and Results link */}
                            <div className="col-span-2 flex flex-col items-start space-y-2">
                                <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-blue-950">{statusText}</span>
                                    {status === 'Normal' || status === 'Optimal' ? (
                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-[#42D4B0] flex items-center justify-center">
                                            <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                                <path
                                                    d="M1 3L2.5 4.5L7 0.5"
                                                    stroke="#42D4B0"
                                                    strokeWidth="1.2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    ) : status === 'High' ? (
                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-[#F15E87] flex items-center justify-center">
                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                <path
                                                    d="M1 0V6.5M1 8.5V9.5"
                                                    stroke="#F15E87"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </div>
                                    ) : status === 'Low' || status === 'Elevated' ? (
                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-[#FFBE7A] flex items-center justify-center">
                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                <path
                                                    d="M1 0V6.5M1 8.5V9.5"
                                                    stroke="#FFBE7A"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </div>
                                    ) : status === 'Borderline' ? (
                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-[#FFBE7A] flex items-center justify-center">
                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                <path
                                                    d="M1 0V6.5M1 8.5V9.5"
                                                    stroke="#FFBE7A"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                />
                                            </svg>
                                        </div>
                                    ) : (
                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-500 flex items-center justify-center">
                                            <svg width="2" height="2" viewBox="0 0 2 2" fill="none">
                                                <circle cx="1" cy="1" r="0.8" fill="gray" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                <button
                                    onClick={() => handleResultsClick(biomarker)}
                                    className="flex items-center space-x-1 text-sm text-blue-950 hover:text-blue-800 transition-colors cursor-pointer">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                    </svg>
                                    <span>Results</span>
                                </button>
                            </div>
                        </div>

                        {/* Mobile Layout: Two Rows */}
                        <div className="md:hidden space-y-3">
                            {/* First Row: Test name, result, status, and results link */}
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-950 mb-1">
                                        {cleanBiomarkerName(biomarker.test_name)}
                                    </h4>
                                    <div className="flex items-baseline space-x-1">
                                        <span className="text-lg font-semibold text-blue-950">
                                            {(() => {
                                                const num = parseFloat(biomarker.result);
                                                return isNaN(num) ? biomarker.result : num.toFixed(1);
                                            })()}
                                        </span>
                                        {biomarker.units && (
                                            <span className="text-lg font-semibold text-blue-950">
                                                {biomarker.units}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-sm font-medium text-blue-950">{statusText}</span>
                                        {status === 'Normal' || status === 'Optimal' ? (
                                            <div className="w-4 h-4 rounded-full bg-white border-2 border-[#42D4B0] flex items-center justify-center">
                                                <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                                    <path
                                                        d="M1 3L2.5 4.5L7 0.5"
                                                        stroke="#42D4B0"
                                                        strokeWidth="1.2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        ) : status === 'High' ? (
                                            <div className="w-4 h-4 rounded-full bg-white border-2 border-[#F15E87] flex items-center justify-center">
                                                <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                    <path
                                                        d="M1 0V6.5M1 8.5V9.5"
                                                        stroke="#F15E87"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>
                                        ) : status === 'Low' || status === 'Elevated' ? (
                                            <div className="w-4 h-4 rounded-full bg-white border-2 border-[#FFBE7A] flex items-center justify-center">
                                                <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                    <path
                                                        d="M1 0V6.5M1 8.5V9.5"
                                                        stroke="#FFBE7A"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>
                                        ) : status === 'Borderline' ? (
                                            <div className="w-4 h-4 rounded-full bg-white border-2 border-[#FFBE7A] flex items-center justify-center">
                                                <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                    <path
                                                        d="M1 0V6.5M1 8.5V9.5"
                                                        stroke="#FFBE7A"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </div>
                                        ) : (
                                            <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-500 flex items-center justify-center">
                                                <svg width="2" height="2" viewBox="0 0 2 2" fill="none">
                                                    <circle cx="1" cy="1" r="0.8" fill="gray" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => handleResultsClick(biomarker)}
                                        className="flex items-center space-x-1 text-sm text-blue-950 hover:text-blue-800 transition-colors cursor-pointer">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                            />
                                        </svg>
                                        <span>Results</span>
                                    </button>
                                </div>
                            </div>

                            {/* Second Row: Historical trend chart */}
                            <div className="w-full">
                                <div className="h-16 w-full rounded-lg border-mld-panel-border border overflow-hidden">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart
                                            data={(() => {
                                                // Transform data to match single results page format
                                                const histData = biomarker.historicalData || [];
                                                const currentValue = parseFloat(biomarker.result) || 0;

                                                // Take last 3 historical points and add current
                                                const transformedData = [
                                                    ...histData.slice(-3).map((point: any) => ({
                                                        name: point.label || point.month || point.date,
                                                        value: point.value,
                                                    })),
                                                    {
                                                        name: 'Current',
                                                        value: currentValue,
                                                    },
                                                ];

                                                return transformedData;
                                            })()}
                                            margin={{top: 6, right: 0, left: 0, bottom: 6}}>
                                            <defs>
                                                <linearGradient
                                                    id={`${chartGradientId}-mobile`}
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1">
                                                    <stop
                                                        offset="5%"
                                                        stopColor={gradientColors.primary}
                                                        stopOpacity={0.3}
                                                    />
                                                    <stop
                                                        offset="95%"
                                                        stopColor={gradientColors.secondary}
                                                        stopOpacity={0.05}
                                                    />
                                                </linearGradient>
                                            </defs>
                                            <XAxis hide />
                                            <Area
                                                type="monotone"
                                                dataKey="value"
                                                stroke="none"
                                                fill={`url(#${chartGradientId}-mobile)`}
                                                connectNulls={false}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="value"
                                                stroke={gradientColors.primary}
                                                strokeWidth={1}
                                                dot={false}
                                                activeDot={false}
                                            />
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default BiomarkerDetailChart;
